<template>
  <div class="collapsable">
    <div class="collapsable-header" :style="headerCss" :class="toggle ? 'hidden' : 'expanded' "
    @click="emitToggle()">
      <div class="icon-container">
        <iconComponent symbol="collapseArrow" :width="30" :class="toggle ? 'up' : 'down' " />
      </div>
      <div class="collapsable-header_slot">
        <!-- @slot Use this slot to insert content for the header div-->
        <slot name="header">
          No header found
        </slot>
      </div>
    </div>

    <div class="collapsable-content" :style="contentCss" v-show="!toggle">
      <transition v-if="slotTransName !== ''" :name="slotTransName" :appear="slotTransAppear" :mode="slotTransMode">
        <!-- @slot Use this slot to insert content for the content div-->  
        <slot name="content">
          No content found
        </slot>
      </transition>

      <slot name="content" v-if="slotTransName === ''">
        No content found
      </slot>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType, type CSSProperties } from 'vue';
import iconComponent from '../IconComponent/IconComponent.vue';
import type { HeaderInterface, ContentInterface } from './Interfaces';

export default defineComponent({
  name: 'Collapsable',
  components: { iconComponent },
  data() {
    return {
      toggle: this.collapsed,
    };
  },
  props: {
   /**     
     * This prop is used to define the transition name,
     * the animation used is built-in in Vue
     * so no complicated JS is needed, just some CSS.
     * Read more about it here: https://v2.vuejs.org/v2/guide/transitions.html
     */
    slotTransName: {
      type: String as PropType<string>,
      required: false,
      default: '',
    },
    /**     
     * This prop is used to define the transition name,
     * the animation used is built-in in Vue
     * so no complicated JS is needed, just some CSS.
     * Read more about it here: https://v2.vuejs.org/v2/guide/transitions.html
     */
    slotTransAppear: {
      type: Boolean as PropType<boolean>,
      required: false,
      default: false,
    },
    /**     
     * This prop is used to define the transition name,
     * the animation used is built-in in Vue
     * so no complicated JS is needed, just some CSS.
     * The options are: "default" | "in-out" | "out-in".
     * Read more about it here: https://v2.vuejs.org/v2/guide/transitions.html
     */
    slotTransMode: {
      type: String as PropType<'default' | 'in-out' | 'out-in'>,
      required: false,
      default: undefined,
    },
    /**
     * This prop defines the background color and
     * position of the text on the Header div
     */
    header: {
      type: Object as PropType<HeaderInterface>,
      required: true,
      default: () => ({
        bgColor: 'transparent',
        textPosition: 'left',
      }),
    },
    /**
     * This prop defines the background color and
     * position of the text on the Content div
     */
    content: {
      type: Object as PropType<ContentInterface>,
      required: false,
      default: () => ({
        bgColor: 'transparent',
        textPosition: 'center',
      }),
    },
    /**
     * This prop defines the default state of the component,
     * meaning if the content should be hidden by default or not
     * when entering a page.
     */
    collapsed: {
      type: Boolean,
      required: false,
      default: true,
    }
  },
  watch: {
    // Lets a parent force-close/open this instance after mount, e.g. to
    // only keep one Collapsable expanded at a time in a list.
    collapsed(newVal: boolean) {
      this.toggle = newVal;
    },
  },
  computed: {
    headerCss(): CSSProperties {
      return {
        '--header-bg-color':
          typeof this.header.bgColor === 'undefined'
            ? 'transparent'
            : this.header.bgColor,
        '--header-text-align':
          typeof this.header.textPosition === 'undefined'
            ? 'left'
            : this.header.textPosition,
      } as CSSProperties;
    },
    contentCss(): CSSProperties {
      return {
        '--content-bg-color':
          typeof this.content.bgColor === 'undefined'
            ? 'transparent'
            : this.content.bgColor,
        '--content-text-align':
          typeof this.content.textPosition === 'undefined'
            ? 'center'
            : this.content.textPosition,
      } as CSSProperties;
    },
  },
  methods: {
    emitToggle() {
      this.toggle = !this.toggle; 
    /**
     * This methods toggles the toggle variable 
     * from false to true and viceversa,
     * it also does an emit 
     * @event toggle
     * @type {boolean} boolean
     */
      this.$emit('toggle', this.toggle);
    }
  },
});
</script>

<style lang="scss">
.collapsable {
  display: flex;
  flex-direction: column;
  align-items: stretch;

  .collapsable-header {
    display: flex;
    background: var(--header-bg-color);
    justify-content: space-between;
    align-items: center;
    border-radius: 8px 8px 0 0;
    cursor: pointer;

    .collapsable-header_slot {
      width: 89% !important;
      justify-content: var(--header-text-align);
      display: flex;
      padding: 16px;
    }

    .icon-container {
      order: 2;
      width: 11%;
      left: -5px;
      position: relative;
      background: var(--header-bg-color);
      display: flex;
      align-items: center;
      justify-content: center;

      .up {
        transform: rotate(-180deg);
        transition: all 0.2s;
      }

      .down {
        transition: all 0.2s;
      }
    }
  }

  .collapsable-content {
    justify-content: var(--content-text-align);
    background: var(--content-bg-color);
    overflow: hidden;
    width: 100%;
    border-radius: 0 0 8px 8px;
  }

  // STATES //
  .expanded {
    border-radius: 8px 8px 0 0 !important;
  }

  .hidden {
    border-radius: 8px;
  }
}
</style>